/**
 * Sends one already-vetted public-site event to the DebtDrone API.
 *
 * Callers are `funnel.ts` and `PublicAnalytics.tsx`. Anything else importing
 * this module skips the allow-list, which the coverage test forbids. The
 * request carries no auth header and no cookie: a logged-in visitor browsing
 * the marketing site must not attach their account to this stream.
 */
export interface AnalyticsReport {
  name: string;
  path: string;
  properties?: Record<string, string>;
}

const SESSION_KEY = 'dd_website_session';

function sessionId(): string | null {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return null;
  }
}

export function reportAnalytics(report: AnalyticsReport): void {
  const base = import.meta.env.VITE_API_BASE_URL;
  if (!base) return;
  const session = sessionId();
  if (!session) return;

  const url = `${base.replace(/\/$/, '')}/api/analytics/collect`;
  const body = JSON.stringify({
    session_id: session,
    name: report.name,
    path: report.path,
    properties: report.properties ?? {},
  });

  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {
    // A blocked request must not surface in the page.
  });
}
